import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Input, Modal } from '@cognitiv/cyprus-ui';
import { BottomNavigation } from 'components/bottom-navigation/BottomNavigation';
import { ModalHeader } from 'components/modal-header/ModalHeader';
import { selectBatchDefinitionParameters } from 'ducks/bacth-definition-parameters/selectors';
import { selectBatchDefinition } from 'ducks/batch-definition/selectors';
import { runBatchDefinition } from 'ducks/batch-definitions/operators';
import { useAppDispatch, useAppSelector } from 'ducks/hooks';
import { selectRunBatchDefinition } from 'ducks/modals/selectors';
import { setModal } from 'ducks/modals/slices';
import { clearScheduledBatch } from 'ducks/scheduled-batch/slices';
import { updateSettings, updateSuccess } from 'ducks/settings/slices';
import { ERROR_LEVEL } from 'ducks/types/common-types';
import { BatchDefinitionPayload } from 'ducks/types/payload-types';
import { ProjectParameters } from 'ducks/types/store-types';
import { useValidate } from 'hooks/useValidation';
import { batch_definition_default_state, batch_definition_validation } from 'modals/batches/constants';
import useForceUpdate from 'modals/parameters/components/hooks/useForceUpdate';
import { Parameters } from 'modals/parameters/components/Parameters';
import { shallowEqual } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getValidationError, handleError } from 'utils/errors';
import { createParameters, getFormWarnings } from 'utils/helpers';
import { mergeSearchQuery } from 'utils/queries';
import { v4 as uuidv4 } from 'uuid';

import { useStepper } from './hooks/usestepper';

import cn from 'modals/batches/components/Components.module.scss';

export const RunBatchDefinition = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const batch_definition = useAppSelector(selectBatchDefinition);
  const parameters = useAppSelector(selectBatchDefinitionParameters);
  const run_batch_definition = useAppSelector(selectRunBatchDefinition, shallowEqual);

  const [batch_parameters, setBatchParameters] = useState<ProjectParameters>([]);
  const { force_update, handleUpdateDataFromModal } = useForceUpdate(setBatchParameters);
  const { page, wizard_width, reset, onNextStep, onPrevStep } = useStepper(0);

  const [form, setForm] = useState<BatchDefinitionPayload>({ ...batch_definition_default_state });
  const { errors, validate, resetFieldError } = useValidate(form, batch_definition_validation);

  useEffect(() => {
    setForm({ ...batch_definition_default_state, ...batch_definition });
  }, [batch_definition, reset]);

  useEffect(() => {
    setBatchParameters(parameters);
  }, [parameters]);

  const onChange = useCallback(
    (item: Partial<BatchDefinitionPayload>) => {
      resetFieldError(item);
      setForm((form) => ({ ...form, ...item }));
    },
    [resetFieldError],
  );

  const onNext = useCallback(() => {
    const is_valid = validate();

    if (!is_valid && !!errors.length) {
      dispatch(setModal({ warning: true }));
      dispatch(updateSettings({ warning: getFormWarnings(errors) }));
    }

    if (is_valid) {
      onNextStep();
    }
  }, [dispatch, errors, onNextStep, validate]);

  const { batch_definition_name } = form;

  const closeModal = useCallback(() => {
    reset();
    dispatch(setModal({ run_batch_definition: false }));
    dispatch(clearScheduledBatch());
  }, [dispatch, reset]);

  const onSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const is_valid = validate();

      if (!is_valid) return;

      try {
        const queries = mergeSearchQuery(search, { update: uuidv4() });

        const { batch_definition_id } = batch_definition;

        if (!batch_definition_id) return;

        const payload = {
          batch_definition_name,
          parameters: createParameters(batch_parameters),
        };

        const res: any = await runBatchDefinition(batch_definition_id, payload);
        if (res?.error) return;

        closeModal();
        navigate(`${pathname}${queries}`);
        dispatch(updateSuccess(`Batch Definition is running successfully`));
      } catch (err) {
        handleError(err);
      }
    },
    [
      batch_definition,
      batch_definition_name,
      batch_parameters,
      closeModal,
      dispatch,
      navigate,
      pathname,
      search,
      validate,
    ],
  );

  const setParameters = useCallback(
    (row: ProjectParameters[number], force_update?: boolean) => {
      handleUpdateDataFromModal(row, force_update);
    },
    [handleUpdateDataFromModal],
  );

  const handleReturn = useCallback(() => {
    if (page === 1) {
      onPrevStep();
    } else {
      closeModal();
    }
  }, [closeModal, onPrevStep, page]);

  return (
    <Modal
      name="run_batch_definition"
      show={run_batch_definition}
      width={wizard_width}
      padding={0}
      close_box={50}
      onClose={closeModal}
      standard={false}
    >
      <ModalHeader onReturn={handleReturn} page={page}>
        Run Batch Definition
      </ModalHeader>
      <form className={cn.overrideWizard} onSubmit={onSubmit}>
        {page === 0 && (
          <Input
            auto_focus
            label="Batch Name"
            value={batch_definition_name}
            onChange={(value: string) => onChange({ batch_definition_name: value })}
            error_level={getValidationError({ errors, key: 'batch_definition_name' }) ? ERROR_LEVEL.ERROR : undefined}
          />
        )}
        {page === 1 && (
          <Parameters force_update={force_update} setParameters={setParameters} parameters={batch_parameters} />
        )}
        <BottomNavigation current={page} end={1} onBack={onPrevStep} onNext={onNext} onClose={closeModal} />
      </form>
    </Modal>
  );
};
